/**
 * service-mail cash-reconciliation client. Mirrors:
 *   apps/service-mail/src/modules/cash-reconciliation/cash-reconciliation.controller.ts
 *
 * RBAC:
 *   submit + list — Mail/submitCashReconciliation.
 *   finalize     — Mail/finalizeCashReconciliation.
 */
import { env } from '../env';

import { request } from './client';

export type CashReconciliationStatus = 'DRAFT' | 'SUBMITTED' | 'RECONCILED' | 'DISCREPANCY';

export interface CashReconciliationDto {
  readonly id: string;
  readonly operatorId: string;
  readonly outletCode: string;
  readonly shiftId: string | null;
   
  readonly openingBalanceMinor: number;
   
  readonly cashInMinor: number;
   
  readonly cashOutMinor: number;
   
  readonly expectedClosingMinor: number;
   
  readonly actualClosingMinor: number;
   
  readonly varianceMinor: number;
  readonly status: CashReconciliationStatus;
  readonly submittedAt: string | null;
  readonly reconciledByOperatorId: string | null;
  readonly reconciledAt: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListCashReconciliationsResponse {
  readonly items: readonly CashReconciliationDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SubmitReconciliationBody {
  readonly outletCode: string;
  readonly shiftId?: string;
   
  readonly openingBalanceMinor: number;
   
  readonly cashInMinor: number;
   
  readonly cashOutMinor: number;
   
  readonly actualClosingMinor: number;
  readonly notes?: string;
}

export interface ListCashReconciliationsQuery {
  readonly operatorId?: string;
  readonly outletCode?: string;
  readonly status?: CashReconciliationStatus;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function submitCashReconciliation(body: SubmitReconciliationBody): Promise<CashReconciliationDto> {
  return request<CashReconciliationDto>(baseUrl(), '/v1/cash-reconciliations', { method: 'POST', body });
}

export async function listCashReconciliations(
  query: ListCashReconciliationsQuery = {},
): Promise<ListCashReconciliationsResponse> {
  return request<ListCashReconciliationsResponse>(baseUrl(), '/v1/cash-reconciliations', {
    method: 'GET',
    query: {
      operatorId: query.operatorId,
      outletCode: query.outletCode,
      status: query.status,
      from: query.from,
      to: query.to,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function finalizeCashReconciliation(id: string): Promise<CashReconciliationDto> {
  return request<CashReconciliationDto>(baseUrl(), `/v1/cash-reconciliations/${id}/finalize`, {
    method: 'POST',
    body: {},
  });
}
